/**
 * BrasArmeAnalysisPanel — Modal d'analyse "Identifier les grands acteurs compatibles".
 *
 * Pattern job-based : POST /workspace/bras-arme/analyze → renvoie job_id,
 * polling toutes les 2s sur /workspace/bras-arme/jobs/{job_id} jusqu'à done|failed.
 *
 * Le panel se déclenche depuis Origination, Asymétries (UtilityMapping) ou
 * l'auto-bootstrap onboarding. Source précisée via prop sourceModule.
 *
 * Affichage : liste verticale de 5-8 candidats classés par score_compatibility.
 * Pour chaque candidat : nom + type + score + 4 sections (besoin, problème résolu,
 * accès, fractale) + badge "Aussi source de capital" si applicable + exemple
 * comparable en footer.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import BrutNetScoreBadge from "@/components/BrutNetScoreBadge";
import { notify, fmtError } from "@/lib/notify";
import { Sparkles, Loader2, X, Crown, Target, Route, Coins, BookOpen, RefreshCw, Check, AlertTriangle, UserPlus } from "lucide-react";
import FreshnessBadge from "./FreshnessBadge";

const TYPE_LABELS = {
  banque: { label: "Banque", color: "#1D9E75" },
  assurance: { label: "Assurance", color: "#378ADD" },
  fonds: { label: "Fonds", color: "#9B7AE2" },
  grande_entreprise: { label: "Grande entreprise", color: "#EF9F27" },
  etat: { label: "État", color: "#EFD127" },
  ong: { label: "ONG", color: "#27D1A0" },
  reassureur: { label: "Réassureur", color: "#4F8FE5" },
  family_office: { label: "Family Office", color: "#E27AB5" },
};

const FRACTAL_LABELS = {
  micro: "Micro",
  meso: "Meso",
  macro: "Macro",
};

export default function BrasArmeAnalysisPanel({ open, onClose, sourceModule = "origination", focusHint = "" }) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState("idle"); // idle | running | done | failed
  const [progress, setProgress] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [analysisId, setAnalysisId] = useState(null);
  const [analysisCreatedAt, setAnalysisCreatedAt] = useState(null); // V23.14.7 — freshness
  const [error, setError] = useState(null);
  const [pushingIdx, setPushingIdx] = useState(null);
  const [pushedIds, setPushedIds] = useState({}); // {idx: annuaire_id}
  const pollTimerRef = useRef(null);

  const pushCandidateToAnnuaire = useCallback(async (idx) => {
    if (!analysisId) return;
    setPushingIdx(idx);
    try {
      const r = await api.post(`/workspace/bras-arme/analyses/${analysisId}/push-to-annuaire`, { candidate_index: idx });
      setPushedIds((s) => ({ ...s, [idx]: r.data.id }));
      notify.success(r.data.created
        ? t("bras_arme.pushed_created", { defaultValue: "Ajouté à l'Annuaire avec tag « Super Partenaire »." })
        : t("bras_arme.pushed_existing", { defaultValue: "Entité existante — tag « Super Partenaire » ajouté." }));
    } catch (e) {
      notify.error(fmtError(e));
    } finally {
      setPushingIdx(null);
    }
  }, [analysisId, t]);

  // Fetch latest cercle vertueux on open (au cas où il existe déjà)
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/workspace/bras-arme/cercle-vertueux?top=10");
        if (cancelled) return;
        if (r.data?.available) {
          setCandidates(r.data.candidates || []);
          setAnalysisId(r.data.analysis_id);
          setAnalysisCreatedAt(r.data.created_at || null);
          setPhase("done");
        }
      } catch (e) {
        // 404 = pas d'analyse encore, on reste en idle
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const startAnalysis = useCallback(async () => {
    setPhase("running");
    setProgress(t("bras_arme.starting", { defaultValue: "Démarrage de l'analyse…" }));
    setError(null);
    try {
      const r = await api.post("/workspace/bras-arme/analyze", {
        mode: "hybride",
        source_module: sourceModule,
        focus_hint: focusHint || null,
      });
      const jobId = r.data?.job_id;
      if (!jobId) throw new Error("Job ID manquant");

      // Poll every 2s
      stopPolling();
      pollTimerRef.current = setInterval(async () => {
        try {
          const j = await api.get(`/workspace/bras-arme/jobs/${jobId}`);
          const data = j.data || {};
          setProgress(data.progress || "");
          if (data.status === "done") {
            stopPolling();
            // Fetch candidates
            const detail = await api.get(`/workspace/bras-arme/analyses/${data.analysis_id}`);
            setCandidates(detail.data?.candidates || []);
            setAnalysisId(data.analysis_id);
            setAnalysisCreatedAt(detail.data?.created_at || new Date().toISOString());
            setPhase("done");
            notify.success(t("bras_arme.success", {
              count: data.candidates_count,
              defaultValue: `${data.candidates_count} grands acteurs identifiés.`,
            }));
          } else if (data.status === "failed") {
            stopPolling();
            setPhase("failed");
            setError(data.error || "Erreur inconnue");
          }
        } catch (e) {
          stopPolling();
          setPhase("failed");
          setError(fmtError(e));
        }
      }, 2000);
    } catch (e) {
      setPhase("failed");
      setError(fmtError(e));
      notify.error(fmtError(e));
    }
  }, [sourceModule, focusHint, stopPolling, t]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[90] bg-[#0A1A2F]/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => phase !== "running" && onClose()}
      data-testid="bras-arme-panel"
    >
      <div
        className="w-full max-w-3xl max-h-[90vh] overflow-auto noam-card p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          disabled={phase === "running"}
          className="absolute top-4 right-4 text-zinc-500 hover:text-white disabled:opacity-30"
          data-testid="bras-arme-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-2 mb-1">
          <Crown className="w-5 h-5 text-[#EFD127]" />
          <div>
            <div className="noam-overline">{t("bras_arme.overline", { defaultValue: "Stratégie · Super Partenaires" })}</div>
            <div className="noam-display text-xl font-semibold">
              {t("bras_arme.title", { defaultValue: "Identifier les Super Partenaires & Super Clients" })}
            </div>
          </div>
        </div>
        <p className="text-sm text-zinc-400 mt-2 leading-relaxed">
          {t("bras_arme.subtitle", {
            defaultValue: "Noam croise votre profil cabinet + Annuaire + Doctrine + tendances web pour révéler les grandes institutions (banques, assurances, fonds, grandes entreprises) qui peuvent devenir vos Super Partenaires (vous délèguent une fonction opérationnelle) ou Super Clients (vous achètent du conseil structurant). Exemple canonique : les sociétés hypothécaires sont des Super Partenaires de banques cantonales.",
          })}
        </p>

        {/* V23.15.36 — Doctrine 3Q · état analyse + candidat prioritaire */}
        <div className="mt-4">
          <ThreeQPageHeader endpoint="/workspace/bras-arme/top-priority" />
        </div>

        {/* Action principale */}
        <div className="mt-5 flex items-center gap-2">
          <button
            onClick={startAnalysis}
            disabled={phase === "running"}
            className="btn-primary text-sm disabled:opacity-50"
            data-testid="bras-arme-analyze-btn"
          >
            {phase === "running" ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> {progress || t("bras_arme.running", { defaultValue: "Analyse en cours…" })}</>
            ) : phase === "done" ? (
              <><RefreshCw className="w-4 h-4" /> {t("bras_arme.regenerate", { defaultValue: "Relancer une analyse" })}</>
            ) : (
              <><Sparkles className="w-4 h-4" /> {t("bras_arme.start", { defaultValue: "Lancer l'analyse" })}</>
            )}
          </button>
          {phase === "done" && (
            <span className="text-[11px] text-zinc-500 flex items-center gap-2 flex-wrap">
              <span>
                {candidates.length} {t("bras_arme.candidates_count", { defaultValue: "candidats" })} ·
                {" "}<Link to="/app/bras-arme/history" className="text-[#378ADD] hover:underline" data-testid="bras-arme-history-link">{t("bras_arme.history", { defaultValue: "Historique" })}</Link>
              </span>
              <FreshnessBadge
                lastGeneratedAt={analysisCreatedAt}
                moduleLabel={t("bras_arme.title", { defaultValue: "Super Partenaires" })}
                testid="bras-arme-freshness"
              />
            </span>
          )}
        </div>

        {/* État d'erreur */}
        {phase === "failed" && error && (
          <div className="mt-4 p-3 rounded border border-[#E24B4A]/40 bg-[#E24B4A]/10 text-sm text-[#E24B4A]" data-testid="bras-arme-error">
            {error}
          </div>
        )}

        {/* Résultats */}
        {phase === "done" && candidates.length > 0 && (
          <div className="mt-5 space-y-3" data-testid="bras-arme-results">
            {candidates.map((c, i) => {
              const typeStyle = TYPE_LABELS[c.type] || { label: c.type, color: "#71717A" };
              return (
                <div
                  key={i}
                  className="noam-card p-4 border border-white/5 hover:border-white/10 transition"
                  data-testid={`bras-arme-candidate-${i}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-base font-semibold text-white">{c.name}</span>
                        <span
                          className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border"
                          style={{ color: typeStyle.color, borderColor: `${typeStyle.color}66`, background: `${typeStyle.color}1a` }}
                        >
                          {typeStyle.label}
                        </span>
                        {c.also_capital_source && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#EFD127] bg-[#EFD127]/10 border border-[#EFD127]/30 rounded px-2 py-0.5"
                            title={t("bras_arme.also_capital_hint", { defaultValue: "Cet acteur est aussi une source de capital potentielle (financement, participation, co-investissement)." })}
                            data-testid={`bras-arme-candidate-${i}-capital-badge`}
                          >
                            <Coins className="w-3 h-3" /> {t("bras_arme.also_capital", { defaultValue: "Aussi source de capital" })}
                          </span>
                        )}
                      </div>
                      {c.sector && <div className="text-[11px] text-zinc-500 mt-1">{c.sector}</div>}
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t("bras_arme.compat_score", { defaultValue: "Compatibilité" })}</div>
                      <BrutNetScoreBadge
                        brute={c.score_compatibility}
                        testId={`bras-arme-score-brutnet-${i}`}
                      />
                      {/* V23.14.4 — Push to Annuaire en 1 clic */}
                      <button
                        type="button"
                        onClick={() => pushCandidateToAnnuaire(i)}
                        disabled={pushingIdx !== null}
                        className={`mt-2 inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border transition ${
                          pushedIds[i]
                            ? "border-[#1D9E75]/40 bg-[#1D9E75]/10 text-[#1D9E75]"
                            : "border-white/10 text-zinc-400 hover:border-[#378ADD]/60 hover:text-[#378ADD] disabled:opacity-50"
                        }`}
                        data-testid={`bras-arme-candidate-${i}-push-btn`}
                        title={t("bras_arme.push_to_annuaire_hint", { defaultValue: "Ajoute cet acteur à votre Annuaire avec le tag « Super Partenaire »." })}
                      >
                        {pushingIdx === i ? (
                          <><Loader2 className="w-3 h-3 animate-spin" /> {t("bras_arme.push_running", { defaultValue: "Ajout…" })}</>
                        ) : pushedIds[i] ? (
                          <><Check className="w-3 h-3" /> {t("bras_arme.push_done", { defaultValue: "Dans l'Annuaire" })}</>
                        ) : (
                          <><UserPlus className="w-3 h-3" /> {t("bras_arme.push_btn", { defaultValue: "Ajouter à l'Annuaire" })}</>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="mt-3 grid sm:grid-cols-2 gap-3 text-[12px] text-zinc-300">
                    <Section icon={<Target className="w-3 h-3 text-[#378ADD]" />} label={t("bras_arme.needs_arm", { defaultValue: "Besoin d'un bras opérationnel" })}>
                      {c.reason_needs_arm}
                    </Section>
                    <Section icon={<Sparkles className="w-3 h-3 text-[#1D9E75]" />} label={t("bras_arme.problem_solved", { defaultValue: "Problème structurel résolu" })}>
                      {c.structural_problem_solved}
                    </Section>
                    <Section icon={<Route className="w-3 h-3 text-[#EF9F27]" />} label={t("bras_arme.access_path", { defaultValue: "Chemin d'accès" })}>
                      {c.access_path}
                    </Section>
                    <Section icon={<BookOpen className="w-3 h-3 text-[#9B7AE2]" />} label={t("bras_arme.example", { defaultValue: "Exemple comparable" })}>
                      {c.comparable_example}
                    </Section>
                  </div>

                  <div className="mt-2 text-[10px] uppercase tracking-wider text-zinc-600">
                    {t("bras_arme.fractal_level", { defaultValue: "Niveau fractale" })} : <span className="text-zinc-400">{FRACTAL_LABELS[c.fractal_level] || c.fractal_level}</span>
                  </div>

                  {/* V23.14.2 — Doctrine conformity scoring */}
                  {((c.doctrines_respected && c.doctrines_respected.length > 0) || (c.doctrines_at_risk && c.doctrines_at_risk.length > 0)) && (
                    <div className="mt-4 pt-3 border-t border-white/5 space-y-2">
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
                        {t("bras_arme.doctrine_check", { defaultValue: "Conformité doctrine" })}
                      </div>
                      {c.doctrines_respected && c.doctrines_respected.length > 0 && (
                        <div className="space-y-1.5" data-testid={`bras-arme-candidate-${i}-doctrines-respected`}>
                          {c.doctrines_respected.map((d, j) => (
                            <div key={`r-${j}`} className="flex items-start gap-2 text-[11px]">
                              <Check className="w-3 h-3 text-[#1D9E75] shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <div className="text-zinc-200">
                                  <span className="font-mono text-[#1D9E75] mr-1">#{d.principle_id}</span>
                                  <span className="font-medium">{d.principle_title}</span>
                                </div>
                                <div className="text-zinc-500 leading-snug mt-0.5">{d.explanation}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {c.doctrines_at_risk && c.doctrines_at_risk.length > 0 && (
                        <div className="space-y-1.5" data-testid={`bras-arme-candidate-${i}-doctrines-at-risk`}>
                          {c.doctrines_at_risk.map((d, j) => (
                            <div key={`a-${j}`} className="flex items-start gap-2 text-[11px]">
                              <AlertTriangle className="w-3 h-3 text-[#EF9F27] shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <div className="text-zinc-200">
                                  <span className="font-mono text-[#EF9F27] mr-1">#{d.principle_id}</span>
                                  <span className="font-medium">{d.principle_title}</span>
                                  <span className="text-[10px] text-[#EF9F27] uppercase tracking-wider ml-1.5">{t("bras_arme.at_risk", { defaultValue: "à risque" })}</span>
                                </div>
                                <div className="text-zinc-500 leading-snug mt-0.5">{d.explanation}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {phase === "idle" && (
          <div className="mt-6 text-center text-sm text-zinc-500" data-testid="bras-arme-idle">
            {t("bras_arme.idle_hint", { defaultValue: "Aucune analyse pour le moment. Cliquez sur « Lancer l'analyse » pour démarrer." })}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

function Section({ icon, label, children }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-[12px] text-zinc-300 leading-snug">{children}</div>
    </div>
  );
}
